import React, { useEffect, useState } from "react";
import { useAuth } from '../AuthContext';
import { motion } from "framer-motion";
import http from '../../Api/http';
import { PackageCheck, ArrowLeft, Truck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../card/Card"; // عدّل المسار حسب مشروعك
import Util from "../../Util";
import Shipment from "./Shipment";

const EmployeeDashboard = () => {
    const { t, i18n } = useTranslation();
    const { user } = useAuth();
    const [shipments, setShipments] = useState([]);
    const [shipmentId, setShipmentId] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [openShipmentDialog, setOpenShipmentDialog] = useState(false);

    useEffect(() => {
        if (user?.ID) {
            loadShipments();
        }
    }, [user]);

    const loadShipments = async () => {
        try {
            setIsLoading(true);
            const res = await http.getJsonAxios(http.actions.Shipments.GetOrdersForReceipt, null, t);
            if (res.status === 200 && res.data.success) {
                setShipments(res.data.obj);
            }
        } catch { }
        finally {
            setIsLoading(false);
        }
    };

    // شحنات مخصصة للموظف
    const pendingReceipts = shipments.filter(
        (s) => s.StatusId === 2 && s.EmployeeId === user?.ID
    );

    return (
        <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto" dir={t('dir')}>
            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-16"
            >
                <h1 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900 dark:text-white">
                    {t('receivingShipments')}
                </h1>
                <div className="h-1.5 w-24 bg-accent mx-auto rounded-full" />
                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                    {t('receiveShipmentSubTitle')}
                </p>
            </motion.div>

            {/* Loading Skeleton */}
            {isLoading ? (
                <div className="space-y-4">
                    {[1, 2].map((i) => (
                        <Card key={i} className="h-32 animate-pulse bg-gray-200 dark:bg-gray-700" />
                    ))}
                </div>
            ) : pendingReceipts.length === 0 ? (
                <Card className="border-dashed border-2 border-gray-400 dark:border-gray-600 bg-transparent flex flex-col items-center justify-center p-12 text-center">
                    <Truck className="w-12 h-12 text-gray-400/50 mb-4" />
                    <h3 className="text-xl font-bold text-foreground mb-1">{t('noReceivingTasks')}</h3>
                    <p className="text-muted-foreground">
                        {t('noShipments')}
                    </p>
                </Card>
            ) : (
                <div className="space-y-4">
                    {pendingReceipts.map((shipment, i) => (
                        <motion.div
                            key={shipment.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.1 }}
                        >
                            <div onClick={() => {
                                setShipmentId(shipment.Id);
                                setOpenShipmentDialog(true);
                            }}>
                                <Card className="group hover:border-emerald-500/30 hover:shadow-lg hover:shadow-emerald-500/5 transition-all cursor-pointer">
                                    <CardContent className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center font-mono font-bold text-lg text-foreground border border-gray-200 dark:border-gray-600">
                                                {shipment.Id}
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-lg mb-1">
                                                    {t('shipmentFrom')}: {shipment.supplierName}
                                                </h4>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    {t('sentAt')}: {Util.formatDate(shipment.SentDate, i18n.language)}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-4 text-emerald-600 font-bold bg-emerald-50 dark:bg-emerald-900/10 px-4 py-2 rounded-lg group-hover:bg-emerald-100 transition-colors">
                                            {t('openAndReceive')}
                                            <ArrowLeft className="w-5 h-5 rtl:-scale-x-100 transition-transform group-hover:-translate-x-1" />
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}
            {/* Shipment Dialog */}
            {
                openShipmentDialog &&
                <Shipment
                    open={openShipmentDialog}
                    onClose={() => {
                        setOpenShipmentDialog(false);
                        loadShipments();
                    }}
                    shipmentId={shipmentId}  // 0 تعني شحنة جديدة
                />
            }
        </div>
    );
}

export default EmployeeDashboard;